import { defineRecipe } from '@plurall/elo-v3';

export const navLinkRecipe = defineRecipe({
  className: 'item',
  base: {
    '--item-size': '40px',
    '--item-icon-size': '24px',
    '--item-label-color': 'var(--sidebar-text-color)',
    '--item-icon-color': 'var(--sidebar-icon-color)',

    display: 'grid',
    gap: 'space.12',
    gridTemplateColumns: 'var(--item-icon-size) minmax(0, 1fr) var(--item-icon-size)',
    inlineSize: '100%',
    minBlockSize: 'var(--item-size)',
    padding: 'padding.xsmall',
    borderRadius: 'border.radius.xsmall',
    textDecoration: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color .15s ease-in-out, color .15s ease-in-out',
    '&:hover, &:focus-visible': {
      background: 'alpha.teal.16',
      color: 'foreground.brand',
    },
    '&:focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: 'action.focus.ring.primary',
      outlineOffset: '2px',
    },
    '[data-open="true"] &, [data-open="false"]:hover &': {
      sm: {
        transition:
          'background-color var(--sidebar-transition-duration) ease-in-out, ' +
          'color var(--sidebar-transition-duration) ease-in-out, ' +
          'inline-size var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1), ' +
          'grid-template-columns var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1), ' +
          'gap var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1), ' +
          'border-radius var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1)',
      },
    },
    '[data-open="false"]:not(:hover) &': {
      sm: {
        blockSize: 'var(--item-size)',
        inlineSize: 'var(--item-size)',
        borderRadius: 'calc(var(--item-size) / 2)',
        overflow: 'clip',
        gap: '0',
        gridTemplateColumns: 'var(--item-icon-size) 0 0',
      },
    },
    sm: {
      transition:
        'background-color .15s ease-in-out, color .15s ease-in-out, ' +
        'inline-size var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1), ' +
        'grid-template-columns var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1), ' +
        'gap var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1), ' +
        'border-radius var(--sidebar-transition-duration) cubic-bezier(0.77, 0, 0.18, 1)',
    },
  },
  variants: {
    isActive: {
      true: {
        background: 'alpha.teal.16',
        color: 'foreground.brand',
      },
      false: {},
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

export const navSubitemLinkRecipe = defineRecipe({
  className: 'local-navigation__subitem-link',
  base: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    height: 'size.40',
    width: '100%',
    margin: '0 auto',
    paddingTop: 'space.8',
    paddingRight: 'space.8',
    paddingBottom: 'space.8',
    paddingLeft: '40px',
    borderRadius: '8px',
    border: 0,
    background: 'transparent',
    color: 'text.secondary',
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'sm',
    fontWeight: 'normal',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background-color 150ms ease-in-out, color 150ms ease-in-out',
    '& .local-navigation__label': { fontWeight: 'normal' },
    _hover: {
      background: 'alpha.teal.16',
      color: 'foreground.brand',
      textDecoration: 'none',
    },
    _active: {
      background: 'alpha.teal.24',
      color: 'foreground.brand',
      textDecoration: 'none',
    },
    _focusVisible: {
      position: 'relative',
      background: 'alpha.teal.16',
      color: 'foreground.brand',
      outline: 'none',
      _after: {
        content: '""',
        position: 'absolute',
        inset: '-4px',
        border: '2px solid',
        borderColor: 'action.focus.ring.primary',
        borderRadius: '10px',
        pointerEvents: 'none',
      },
    },
    '@media screen and (max-width: 480px)': {
      height: '48px',
    },
  },
  variants: {
    isActive: {
      true: {
        background: 'alpha.teal.16',
        color: 'foreground.brand',
      },
      false: {},
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

export const navLabelRecipe = defineRecipe({
  className: 'local-navigation__label',
  base: {
    display: 'block',
    alignSelf: 'center',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: 'text.primary',
    fontSize: 'sm',
    fontWeight: 'semibold',
    lineHeight: '19.25px',
    opacity: 1,
    '@media screen and (max-width: 480px)': {
      transform: 'none',
      transition: 'none',
    },
  },
});

export const navIconRecipe = defineRecipe({
  className: 'local-navigation__icon',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 'size.24',
    height: 'size.24',
    color: 'foreground.brand',
    '& :is(svg, path)': {
      color: 'currentColor',
      fill: 'currentColor',
    },
  },
});
